import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Users, X } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar';
import type { UserListQueryParams, UserListStatusFilter } from '@/types';
import {
  Button,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { PermissionGate } from '@/components/common/PermissionGate';
import { usePermissions } from '@/hooks/usePermissions';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { useMe } from '@/features/settings/hooks/useMe';
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter';
import { useUsersList } from '../hooks/useUsers';
import { UserList } from './UserList';
import { UserMatrix } from './UserMatrix';
import { AllStaffDocumentsModal } from './AllStaffDocumentsModal';
import { TablePaginationBar } from '@/components/common/TablePaginationBar';
import { cn } from '@/lib/cn';

const DEFAULT_LIMIT = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
/** Radix Select can't hold an empty value, so "no filter" needs a sentinel. */
const ALL = 'all';

const STATUS_OPTIONS: { value: UserListStatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'exited', label: 'Exited' },
  { value: 'deleted', label: 'Deleted' },
];

type ViewTab = 'users' | 'matrix';

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
}

function buildUserSearchParams(params: UserListQueryParams): URLSearchParams {
  const next = new URLSearchParams();
  if (params.search) next.set('search', params.search);
  if (params.roleId) next.set('roleId', params.roleId);
  if (params.status) next.set('status', params.status);
  if (params.department) next.set('department', params.department);
  if (params.companyId) next.set('companyId', params.companyId);
  if (params.page && params.page > 1) next.set('page', String(params.page));
  if (params.limit && params.limit !== DEFAULT_LIMIT) next.set('limit', String(params.limit));
  return next;
}

function parseUserSearchParams(searchParams: URLSearchParams): UserListQueryParams {
  const status = searchParams.get('status');
  return {
    search: searchParams.get('search')?.trim() || undefined,
    roleId: searchParams.get('roleId') || undefined,
    status: STATUS_OPTIONS.some((option) => option.value === status)
      ? (status as UserListStatusFilter)
      : undefined,
    department: searchParams.get('department')?.trim() || undefined,
    // Comma-separated when several companies are picked; matches the chip filter.
    companyId: searchParams.get('companyId') || undefined,
    page: parsePositiveInt(searchParams.get('page'), 1),
    limit: parsePositiveInt(searchParams.get('limit'), DEFAULT_LIMIT),
  };
}

interface FilterSelectProps {
  label: string;
  value: string | undefined;
  allLabel: string;
  options: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

/** Compact inline select for the filter line, sized to sit beside segmented controls. */
function FilterSelect({
  label,
  value,
  allLabel,
  options,
  onChange,
  disabled,
}: FilterSelectProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <SelectRoot
        value={value ?? ALL}
        onValueChange={(next) => onChange(next === ALL ? undefined : next)}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={`Filter by ${label.toLowerCase()}`}
          className={cn(
            'h-8 min-w-32 gap-1.5 rounded-lg border-gray-300 pl-2.5 pr-2 text-xs font-medium text-gray-700 dark:text-gray-200',
            value && 'border-primary/40 ring-2 ring-primary/10'
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
    </div>
  );
}

export function UsersPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  // useRoles owns the roles:read permission check internally — no enabled flag needed here
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { data: me } = useMe();
  const canViewMatrix = can('roles', 'read');
  const canFilterByCompany =
    can('companies', 'read') &&
    Boolean(me?.roleId && typeof me.roleId !== 'string' && me.roleId.scope === 'all');
  const companyFilter = useWorkspaceCompanyFilter();
  const [activeTab, setActiveTab] = useState<ViewTab>('users');
  const listParams = useMemo(() => parseUserSearchParams(searchParams), [searchParams]);
  const listQueryParams = useMemo<UserListQueryParams>(
    () => ({
      ...listParams,
      companyId: undefined,
      companyIds: canFilterByCompany ? companyFilter.companyIds : undefined,
    }),
    [canFilterByCompany, companyFilter.companyIds, listParams]
  );

  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
    refetch: refetchUsers,
  } = useUsersList(listQueryParams);
  const matrixQuery = useUsersList(
    activeTab === 'matrix' ? { limit: 100 } : { page: 1, limit: 10 },
    { enabled: activeTab === 'matrix' && canViewMatrix }
  );
  const users = listData?.data ?? [];
  const matrixUsers = matrixQuery.data?.data ?? [];
  const total = listData?.total ?? 0;
  const page = listData?.page ?? 1;
  const limit = listData?.limit ?? DEFAULT_LIMIT;
  const totalPages = listData?.totalPages ?? 0;

  const [searchInput, setSearchInput] = useState(listParams.search ?? '');
  const [departmentInput, setDepartmentInput] = useState(listParams.department ?? '');
  const [allDocumentsOpen, setAllDocumentsOpen] = useState(false);

  const updateListSearchParams = useCallback(
    (patch: Partial<UserListQueryParams>): void => {
      setSearchParams((current) =>
        buildUserSearchParams({ ...parseUserSearchParams(current), ...patch })
      );
    },
    [setSearchParams]
  );

  // Back/forward navigation changes the URL under the inputs; follow it.
  useEffect(() => {
    setSearchInput(listParams.search ?? '');
    setDepartmentInput(listParams.department ?? '');
  }, [listParams.search, listParams.department]);

  // The chip filter owns company selection; mirror it into the URL so the view
  // stays shareable, and start from page one whenever it changes.
  const companyParam = canFilterByCompany ? companyFilter.companyIds?.join(',') : undefined;
  useEffect(() => {
    if (!me) return;
    if ((listParams.companyId ?? undefined) === companyParam) return;
    updateListSearchParams({ companyId: companyParam, page: 1 });
    // listParams.companyId is read, not tracked: the URL follows the chips, never the reverse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyParam, me, updateListSearchParams]);

  useEffect(() => {
    const search = searchInput.trim();
    const department = departmentInput.trim();
    if ((listParams.search ?? '') === search && (listParams.department ?? '') === department) {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      updateListSearchParams({
        page: 1,
        search: search || undefined,
        department: department || undefined,
      });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [listParams.search, listParams.department, searchInput, departmentInput, updateListSearchParams]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: role._id, label: role.name })),
    [roles]
  );
  const companyOptions = useMemo(
    () => companyFilter.companies.map((company) => ({ value: company._id, label: company.name })),
    [companyFilter.companies]
  );

  const handleResetFilters = useCallback((): void => {
    setSearchInput('');
    setDepartmentInput('');
    companyFilter.reset();
    setSearchParams(buildUserSearchParams({ page: 1, limit: listParams.limit }));
  }, [companyFilter, listParams.limit, setSearchParams]);

  const hasActiveFilters = Boolean(
    listParams.roleId ||
      listParams.status ||
      departmentInput.trim() ||
      searchInput.trim() ||
      (canFilterByCompany && companyFilter.selectedCompanyIds.size > 0)
  );

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Organization', to: '/dashboard' }, { label: 'Users' }]}
      icon={Users}
      title={activeTab === 'users' ? 'Users' : 'User matrix'}
      description={
        activeTab === 'users'
          ? 'Manage organization users and their roles.'
          : 'View users grouped by role.'
      }
      className="gap-0"
      hideHeader
    >
      <>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ViewTab)}>
          <TabsList
            className="divide-x divide-gray-200 dark:divide-gray-700"
            aria-label="User views"
          >
            <TabsTrigger value="users">Users</TabsTrigger>
            <PermissionGate module="roles" action="read">
              <TabsTrigger value="matrix">User matrix</TabsTrigger>
            </PermissionGate>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <WorkspaceToolbar
              filter={
                canFilterByCompany ? <WorkspaceCompanyChips filter={companyFilter} /> : undefined
              }
              actions={
                <>
                  <PermissionGate module="documents" action="read_staff_others">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setAllDocumentsOpen(true)}
                    >
                      <FileText className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="ml-1.5">All documents</span>
                    </Button>
                  </PermissionGate>
                  <PermissionGate module="users" action="create">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => navigate('/users/new')}
                    >
                      <Plus className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="ml-1.5">Create user</span>
                    </Button>
                  </PermissionGate>
                </>
              }
            >
              <WorkspaceToolbarSearch
                value={searchInput}
                onChange={setSearchInput}
                placeholder="Search by name, email, or employee ID…"
                ariaLabel="Search users"
                active={Boolean(listParams.search)}
              />
            </WorkspaceToolbar>

            {/* One filter line, no card, applied as you choose — same as Members. */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              {roleOptions.length > 0 || rolesLoading ? (
                <FilterSelect
                  label="Role"
                  allLabel="All roles"
                  value={listParams.roleId}
                  options={roleOptions}
                  disabled={rolesLoading}
                  onChange={(roleId) => updateListSearchParams({ roleId, page: 1 })}
                />
              ) : null}

              <FilterSelect
                label="Status"
                allLabel="All statuses"
                value={listParams.status}
                options={STATUS_OPTIONS}
                onChange={(status) =>
                  updateListSearchParams({
                    status: status as UserListStatusFilter | undefined,
                    page: 1,
                  })
                }
              />

              <label className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Department
                </span>
                <input
                  type="text"
                  value={departmentInput}
                  onChange={(event) => setDepartmentInput(event.target.value)}
                  placeholder="Any"
                  className={cn(
                    'h-8 w-36 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 shadow-sm outline-none transition placeholder:font-normal placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:placeholder:text-gray-500',
                    listParams.department && 'border-primary/40 ring-2 ring-primary/10'
                  )}
                />
              </label>

              {hasActiveFilters ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetFilters}
                  className="text-gray-600 dark:text-gray-400"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  <span className="ml-1">Reset</span>
                </Button>
              ) : null}

              <p
                className="ml-auto text-xs text-gray-500 dark:text-gray-400"
                aria-live="polite"
              >
                {listLoading ? 'Loading users…' : `${total} ${total === 1 ? 'user' : 'users'}`}
              </p>
            </div>

            <UserList
              users={users}
              isLoading={listLoading}
              error={listError}
              onRetry={refetchUsers}
              hasActiveFilters={hasActiveFilters}
              onResetFilters={handleResetFilters}
            />

            {!listLoading && !listError ? (
              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={(nextPage) => updateListSearchParams({ page: nextPage })}
                onLimitChange={(nextLimit) => updateListSearchParams({ page: 1, limit: nextLimit })}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                className="px-6 py-3"
              />
            ) : null}
          </TabsContent>

          <TabsContent value="matrix">
            <PermissionGate module="roles" action="read">
              <UserMatrix
                users={matrixUsers}
                roles={roles}
                isLoading={matrixQuery.isLoading || rolesLoading}
                error={matrixQuery.error ?? null}
                onRetry={matrixQuery.refetch}
              />
            </PermissionGate>
          </TabsContent>
        </Tabs>
        <AllStaffDocumentsModal
          open={allDocumentsOpen}
          onClose={() => setAllDocumentsOpen(false)}
          companyOptions={companyOptions}
        />
      </>
    </PageShell>
  );
}

UsersPage.displayName = 'UsersPage';
